import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Pencil, X, Mail } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * DepartmentsManager — full CRUD for departments with inline CC-emails editor.
 * Replaces the generic LookupManager when departments need cc_emails managed
 * (used by ticket_created template + org-level reports).
 */
export default function DepartmentsManager() {
  const { hasPerm } = useAuth();
  const canManage = hasPerm("catalogue.manage_departments");
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank());
  const [ccInput, setCcInput] = useState("");

  function blank() { return { name: "", active: true, cc_emails: [], poc_name: "", poc_email: "" }; }

  const load = () => api.get("/departments").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const startCreate = () => { setEditing(null); setForm(blank()); setCcInput(""); setOpen(true); };
  const startEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name || "",
      active: it.active ?? true,
      cc_emails: Array.isArray(it.cc_emails) ? it.cc_emails : [],
      poc_name: it.poc_name || "",
      poc_email: it.poc_email || "",
    });
    setCcInput("");
    setOpen(true);
  };

  const addCc = (raw) => {
    const email = (raw || "").trim().toLowerCase();
    if (!email) return;
    if (!EMAIL_RE.test(email)) { toast.error("Not a valid email"); return; }
    if (form.cc_emails.includes(email)) { toast.error("Already added"); return; }
    setForm({ ...form, cc_emails: [...form.cc_emails, email] });
    setCcInput("");
  };
  const removeCc = (email) => setForm({ ...form, cc_emails: form.cc_emails.filter((e) => e !== email) });

  const submit = async () => {
    if (!form.name.trim()) return toast.error("Name required");
    // Flush any pending text in the CC input as a final email
    const ccFinal = [...form.cc_emails];
    if (ccInput.trim()) {
      const e = ccInput.trim().toLowerCase();
      if (!EMAIL_RE.test(e)) return toast.error(`"${ccInput}" is not a valid email`);
      if (!ccFinal.includes(e)) ccFinal.push(e);
    }
    const payload = { ...form, cc_emails: ccFinal };
    try {
      if (editing) {
        await api.patch(`/departments/${editing.id}`, payload);
        toast.success("Department updated");
      } else {
        await api.post("/departments", payload);
        toast.success("Department added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete department "${it.name}"?`)) return;
    try {
      await api.delete(`/departments/${it.id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };

  return (
    <div data-testid="departments-manager">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading text-base font-semibold text-slate-900">Departments</h3>
          <p className="text-xs text-slate-500 mt-0.5">
            {items.length} entr{items.length === 1 ? "y" : "ies"} · CC emails are copied on <code className="bg-slate-100 px-1 rounded">ticket_created</code> + org-level reports.
          </p>
        </div>
        {canManage && (
          <Button size="sm" onClick={startCreate} className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-departments">
            <Plus className="w-3.5 h-3.5 mr-1.5" />Add
          </Button>
        )}
      </div>

      <Table>
        <TableHeader>
          <TableRow className="text-xs uppercase tracking-wider text-slate-500">
            <TableHead>Name</TableHead>
            <TableHead>CC emails</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id} className="hover:bg-slate-50">
              <TableCell className="text-sm font-medium text-slate-800">{it.name}</TableCell>
              <TableCell>
                {(it.cc_emails || []).length === 0 ? (
                  <span className="text-xs text-slate-400">—</span>
                ) : (
                  <div className="flex flex-wrap gap-1.5 max-w-[420px]">
                    {(it.cc_emails || []).slice(0, 4).map((e) => (
                      <Badge key={e} variant="outline" className="text-[10px] font-mono bg-indigo-50 text-indigo-700 border-indigo-200">
                        <Mail className="w-2.5 h-2.5 mr-1" />{e}
                      </Badge>
                    ))}
                    {(it.cc_emails || []).length > 4 && (
                      <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-500">
                        +{it.cc_emails.length - 4} more
                      </Badge>
                    )}
                  </div>
                )}
              </TableCell>
              <TableCell>
                <Badge variant="outline" className={`text-xs ${it.active ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-slate-100 text-slate-500"}`}>
                  {it.active ? "Active" : "Inactive"}
                </Badge>
              </TableCell>
              <TableCell className="text-right">
                {canManage ? (
                  <>
                    <Button variant="ghost" size="sm" onClick={() => startEdit(it)} data-testid={`edit-department-${it.id}`}>
                      <Pencil className="w-3.5 h-3.5" />
                    </Button>
                    <Button variant="ghost" size="sm" onClick={() => remove(it)} className="text-rose-600" data-testid={`delete-department-${it.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </>
                ) : (
                  <span className="text-xs text-slate-300">—</span>
                )}
              </TableCell>
            </TableRow>
          ))}
          {items.length === 0 && (
            <TableRow><TableCell colSpan={4} className="text-center text-sm text-slate-400 py-8">No departments</TableCell></TableRow>
          )}
        </TableBody>
      </Table>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? "Edit" : "Add"} Department</DialogTitle>
            <DialogDescription className="text-xs">
              CC emails are added to every <code className="bg-slate-100 px-1 rounded">ticket_created</code> and org-level report email for this department.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                data-testid="department-name-input"
                autoFocus
              />
            </div>

            <div className="space-y-1.5">
              <Label className="flex items-center gap-1.5"><Mail className="w-3.5 h-3.5" />CC emails</Label>
              <div className="flex flex-wrap gap-1.5 mb-2 min-h-[1.75rem]">
                {form.cc_emails.length === 0 && (
                  <span className="text-xs text-slate-400 italic">No CCs yet — these recipients are copied on every ticket_created email.</span>
                )}
                {form.cc_emails.map((e) => (
                  <Badge key={e} variant="outline" className="text-xs font-mono bg-indigo-50 text-indigo-700 border-indigo-200 group">
                    {e}
                    <button
                      type="button"
                      onClick={() => removeCc(e)}
                      className="ml-1.5 text-indigo-500 hover:text-rose-600"
                      data-testid={`remove-cc-${e}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  value={ccInput}
                  onChange={(e) => setCcInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === "," || e.key === " ") {
                      e.preventDefault();
                      addCc(ccInput);
                    }
                  }}
                  placeholder="head-of-ops@company.com"
                  className="font-mono text-xs"
                  data-testid="cc-email-input"
                />
                <Button type="button" variant="outline" size="sm" onClick={() => addCc(ccInput)} data-testid="add-cc-btn">
                  Add
                </Button>
              </div>
              <p className="text-[10px] text-slate-400">Press Enter or comma to add. Click ✕ to remove.</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>POC name <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                <Input
                  value={form.poc_name}
                  onChange={(e) => setForm({ ...form, poc_name: e.target.value })}
                />
              </div>
              <div className="space-y-1.5">
                <Label>POC email <span className="text-slate-400 text-xs font-normal">(optional)</span></Label>
                <Input
                  value={form.poc_email}
                  onChange={(e) => setForm({ ...form, poc_email: e.target.value })}
                  className="font-mono text-xs"
                />
              </div>
            </div>

            <div className="flex items-center gap-3">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label>Active</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="department-submit">
              {editing ? "Save" : "Add"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
